'use client';

import type { Locale } from 'date-fns/locale';
import { enUS, es, pt, zhCN } from 'date-fns/locale';
import { useLocale } from 'next-intl';
import { ComponentProps, useEffect, useMemo, useState } from 'react';
import {
  ClassNames,
  DayPicker,
  formatCaption,
  Matcher,
  PropsBase,
  PropsSingle,
} from 'react-day-picker';
import { Button } from '@/lib/ui-kit/button';
import { cn } from './utils';

// Only import the 4 locales we support
const calLocales: Record<string, Locale> = {
  en: enUS,
  enUS: enUS,
  es: es,
  pt: pt,
  zh: zhCN,
  zhCN: zhCN,
};
import 'react-day-picker/style.css';
import dayjs from 'dayjs';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { AllowedAny } from '@/lib/types/common';
import { Text } from '@/lib/ui-kit/text';

type CalLocaleKey = keyof typeof calLocales;

export type CalendarProps = Omit<
  PropsBase & PropsSingle,
  'mode' | 'disabled' | 'startMonth' | 'endMonth'
> & {
  minDate?: Date;
  maxDate?: Date;
};

const calClassNames: Partial<ClassNames> = {
  months: 'flex flex-col',
  month: 'relative',
  month_caption: 'hidden',
  caption_label: 'hidden',
  nav: 'hidden',
  month_grid: 'w-full border-collapse',
  weekdays: 'grid grid-cols-7 mb-2',
  weekday:
    'flex items-center justify-center size-10 text-[13px] leading-[1.5] font-normal text-v2-text-secondary',
  week: 'grid grid-cols-7',
  day: 'size-10 text-center relative group focus-within:relative focus-within:z-20',
  day_button: cn(
    'flex size-10 cursor-pointer items-center justify-center rounded-[10px] bg-transparent p-0 text-[13px] font-normal leading-[1.5] text-v2-text-secondary transition-colors hover:bg-v2-surface-tertiary hover:text-v2-text-primary',
  ),
  selected: 'rounded-[10px] [&_button]:!text-v2-text-primary [&_button]:!bg-v2-btn-secondary-2',
  today: 'text-v2-text-primary',
  outside: 'opacity-40 pointer-events-none',
  disabled: 'opacity-40 pointer-events-none',
  hidden: 'invisible',
};

function Calendar({
  className,
  classNames,
  minDate,
  maxDate,
  showOutsideDays = true,
  selected,
  onSelect,
  ...props
}: CalendarProps) {
  const [innerDate, setInnerDate] = useState<Date | undefined>(selected);
  const [month, setMonth] = useState(new Date());

  const minMaxProps: Pick<
    ComponentProps<typeof DayPicker>,
    'disabled' | 'startMonth' | 'endMonth'
  > = useMemo(() => {
    const disabledDates: Matcher[] = [];
    if (minDate) disabledDates.push({ before: minDate });
    if (maxDate) disabledDates.push({ after: maxDate });

    return {
      disabled: disabledDates,
      startMonth: minDate,
      endMonth: maxDate,
    };
  }, [minDate, maxDate]);

  const minMonth = minDate;
  const maxMonth = maxDate;
  const prevMonthDisabled =
    minMonth && dayjs(month).subtract(1, 'month').isBefore(minMonth, 'month');
  const nextMonthDisabled = maxMonth && dayjs(month).add(1, 'month').isAfter(maxMonth, 'month');

  const prevYearDisabled = prevMonthDisabled;

  const localeCode = useLocale();
  const calLocale = useMemo(() => {
    const [lang, country] = localeCode.split('-');
    const fullLocale = `${lang}${country}` as CalLocaleKey;
    return calLocales[fullLocale] ?? calLocales[lang as CalLocaleKey] ?? enUS;
  }, [localeCode]);

  const caption = useMemo(() => {
    return formatCaption(month, { locale: calLocale });
  }, [calLocale, month]);

  useEffect(() => {
    setMonth(selected ?? new Date());
    setInnerDate(selected);
  }, [selected]);

  return (
    <div className="flex w-full flex-col gap-2">
      <div className="flex w-full items-center justify-between">
        <div className="flex items-center gap-2">
          <Button
            onClick={() =>
              setMonth((cur) => {
                const before1Year = dayjs(cur).subtract(12, 'month');
                if (minMonth && before1Year.isBefore(minMonth)) {
                  return minMonth;
                }
                return before1Year.toDate();
              })
            }
            disabled={prevYearDisabled}
            variant="icon"
            className="z-30 size-8 p-0 text-v2-text-secondary hover:text-v2-text-primary aria-disabled:cursor-not-allowed aria-disabled:opacity-20"
          >
            <ChevronsLeft />
          </Button>
          <Button
            onClick={() =>
              setMonth((cur) => {
                return dayjs(cur).subtract(1, 'month').toDate();
              })
            }
            disabled={prevMonthDisabled}
            variant="icon"
            className="z-30 size-8 p-0 text-v2-text-secondary hover:text-v2-text-primary aria-disabled:cursor-not-allowed aria-disabled:opacity-20"
          >
            <ChevronLeft />
          </Button>
        </div>

        <Text variant={'label-lg'} className="capitalize text-v2-text-primary">
          {caption}
        </Text>

        <div className="flex items-center gap-2">
          <Button
            onClick={() =>
              setMonth((cur) => {
                return dayjs(cur).add(1, 'month').toDate();
              })
            }
            disabled={nextMonthDisabled}
            variant="icon"
            className="z-30 size-8 p-0 text-v2-text-secondary hover:text-v2-text-primary aria-disabled:cursor-not-allowed aria-disabled:opacity-20"
          >
            <ChevronRight />
          </Button>
          <Button
            onClick={() =>
              setMonth((cur) => {
                const after1Year = dayjs(cur).add(12, 'month');
                if (maxMonth && after1Year.isBefore(maxMonth)) {
                  return maxMonth;
                }
                return after1Year.toDate();
              })
            }
            variant="icon"
            className="z-30 size-8 p-0 text-v2-text-secondary hover:text-v2-text-primary aria-disabled:cursor-not-allowed aria-disabled:opacity-20"
          >
            <ChevronsRight />
          </Button>
        </div>
      </div>
      <DayPicker
        month={month}
        onMonthChange={setMonth}
        animate={false}
        className={className}
        fixedWeeks
        showOutsideDays={showOutsideDays}
        locale={calLocale}
        classNames={{
          ...calClassNames,
          ...classNames,
        }}
        mode="single"
        navLayout="around"
        hideNavigation={true}
        selected={innerDate}
        onSelect={(date) => {
          setInnerDate(date);
          (onSelect as AllowedAny)?.(date);
        }}
        {...minMaxProps}
        {...props}
      />
    </div>
  );
}

Calendar.displayName = 'Calendar';

export { Calendar };
